import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle, Check, Download, FileSpreadsheet, FileText, Loader2, Trash2, Upload,
} from 'lucide-react';
import { Button, Modal } from '../ui';
import { STAT_CARD_BASE } from '../ui/StatCard';
import simulationService from '../../services/simulationService';
import { showError, showSuccess } from '../../utils/toast';

/**
 * Reference documents — upload, then REVIEW WHAT THE AI WILL ACTUALLY SEE.
 *
 * The review step is the reason this panel exists rather than a plain file input.
 * Gemini doesn't read .docx/.xlsx/.pptx natively, so everything is converted to text
 * server-side, and a mangled table silently poisons every downstream stage. The trainer
 * is the only person who can tell that the extraction is wrong — so they get to read and
 * correct it before a single token is spent.
 */

const ICON_BY_KIND = { xlsx: FileSpreadsheet, csv: FileSpreadsheet };

const ReferenceDocumentsPanel = ({ simulationId, onChanged }) => {
  const fileRef = useRef(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [retainOriginal, setRetainOriginal] = useState(false);
  const [reviewing, setReviewing] = useState(null); // { source, text }
  const [savingText, setSavingText] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await simulationService.listSources(simulationId);
      setSources(res.sources || []);
    } catch {
      /* documents are optional — never block the builder on this */
    } finally {
      setLoading(false);
    }
  }, [simulationId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await simulationService.addSource(simulationId, file, { retainOriginal });
      await load();
      onChanged?.();
      if (res.duplicate) {
        showSuccess(res.message || 'That file is already attached');
      } else if (res.source?.warning) {
        // Open the review pane straight away — a warning means the text needs eyes.
        setReviewing({ source: res.source, text: res.extractedText || '' });
      } else {
        showSuccess(`Read ${res.source.chars.toLocaleString()} characters from ${res.source.filename}`);
      }
    } catch (err) {
      showError(err.userMessage || err.message || 'Could not read that file');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const openReview = async (source) => {
    try {
      const res = await simulationService.getSource(simulationId, source.id);
      setReviewing({ source: res.source, text: res.extractedText || '' });
    } catch (err) {
      showError(err.userMessage || 'Could not open that document');
    }
  };

  const saveText = async () => {
    setSavingText(true);
    try {
      await simulationService.updateSource(simulationId, reviewing.source.id, { extractedText: reviewing.text });
      setReviewing(null);
      await load();
      onChanged?.();
      showSuccess('Saved — the AI will use your corrected version');
    } catch (err) {
      showError(err.userMessage || 'Could not save that');
    } finally {
      setSavingText(false);
    }
  };

  const toggleInclude = async (s) => {
    try {
      await simulationService.updateSource(simulationId, s.id, { includeInGeneration: !s.includeInGeneration });
      await load();
      onChanged?.();
    } catch (err) {
      showError(err.userMessage || 'Could not update that');
    }
  };

  const remove = async (s) => {
    try {
      await simulationService.deleteSource(simulationId, s.id);
      await load();
      onChanged?.();
    } catch (err) {
      showError(err.userMessage || 'Could not remove that');
    }
  };

  const download = async (s) => {
    try {
      const res = await simulationService.downloadSource(simulationId, s.id);
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      showError(err.userMessage || 'The original file was not kept');
    }
  };

  return (
    <div className={`${STAT_CARD_BASE} p-5`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            <FileText size={15} strokeWidth={1.75} className="text-neutral-400" />
            Reference documents
          </h3>
          <p className="mt-1 max-w-xl text-xs text-neutral-500 dark:text-neutral-400">
            Process docs, incident reports, an existing workshop deck — anything that makes the
            scenarios sound like your organisation. You&apos;ll see exactly what the AI reads
            before it writes anything.
          </p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.xlsx,.pptx,.txt,.md,.csv"
          className="hidden"
          onChange={(e) => upload(e.target.files?.[0])}
        />
        <Button variant="secondary" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
          {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} strokeWidth={2} className="mr-1.5" />}
          {uploading ? 'Reading…' : 'Attach'}
        </Button>
      </div>

      <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs text-neutral-500 dark:text-neutral-400">
        <input
          type="checkbox"
          checked={retainOriginal}
          onChange={(e) => setRetainOriginal(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 rounded border-neutral-300 text-primary-500 focus:ring-primary-400"
        />
        <span>
          Keep the original file too. Off by default — only the extracted text is needed, and
          these documents are often confidential.
        </span>
      </label>

      {!loading && sources.length > 0 && (
        <div className="mt-4 space-y-2">
          {sources.map((s) => {
            const Icon = ICON_BY_KIND[s.kind] || FileText;
            return (
              <div
                key={s.id}
                className={`rounded-xl border p-3 ${
                  s.includeInGeneration
                    ? 'border-neutral-200 dark:border-neutral-700/60'
                    : 'border-dashed border-neutral-300 opacity-60 dark:border-neutral-700'
                }`}
              >
                <div className="flex flex-wrap items-center gap-2.5">
                  <Icon size={15} strokeWidth={1.75} className="shrink-0 text-neutral-400" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                    {s.filename}
                  </span>
                  <span className="shrink-0 text-[11px] tabular-nums text-neutral-400">
                    {s.chars.toLocaleString()} chars{s.edited ? ' · edited' : ''}
                  </span>
                  <div className="flex shrink-0 gap-1">
                    <button type="button" onClick={() => openReview(s)}
                      className="rounded-lg px-2 py-1 text-[11px] font-semibold text-primary-600 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-500/10">
                      Review text
                    </button>
                    {s.hasOriginal && (
                      <button type="button" onClick={() => download(s)} aria-label="Download original"
                        className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800">
                        <Download size={13} strokeWidth={2} />
                      </button>
                    )}
                    <button type="button" onClick={() => remove(s)} aria-label={`Remove ${s.filename}`}
                      className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600 dark:hover:bg-neutral-800">
                      <Trash2 size={13} strokeWidth={2} />
                    </button>
                  </div>
                </div>

                {s.warning && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 px-2.5 py-1.5 dark:bg-amber-500/10">
                    <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                    <p className="text-[11px] leading-relaxed text-amber-800 dark:text-amber-200">{s.warning}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => toggleInclude(s)}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                >
                  <span className={`flex h-3.5 w-3.5 items-center justify-center rounded border ${
                    s.includeInGeneration ? 'border-primary-500 bg-primary-500 text-white' : 'border-neutral-300 dark:border-neutral-600'
                  }`}>
                    {s.includeInGeneration && <Check size={9} strokeWidth={4} />}
                  </span>
                  Use when writing this simulation
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------- review / correct ---------- */}
      <Modal
        isOpen={Boolean(reviewing)}
        onClose={savingText ? () => {} : () => setReviewing(null)}
        title={reviewing ? `What the AI will read from ${reviewing.source.filename}` : ''}
        size="xl"
      >
        {reviewing && (
          <div className="space-y-3">
            <p className="text-xs leading-relaxed text-neutral-500 dark:text-neutral-400">
              This is the plain text pulled out of your file — tables flatten, layout is lost.
              Fix anything garbled, delete anything irrelevant. Only what&apos;s here reaches the AI.
            </p>
            {reviewing.source.warning && (
              <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-500/10">
                <AlertTriangle size={13} strokeWidth={2} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-xs text-amber-800 dark:text-amber-200">{reviewing.source.warning}</p>
              </div>
            )}
            <textarea
              value={reviewing.text}
              onChange={(e) => setReviewing({ ...reviewing, text: e.target.value })}
              rows={18}
              spellCheck={false}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 font-mono text-xs leading-relaxed text-neutral-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] tabular-nums text-neutral-400">
                {reviewing.text.length.toLocaleString()} characters
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setReviewing(null)} disabled={savingText}>Cancel</Button>
                <Button variant="primary-filled" onClick={saveText} loading={savingText} disabled={savingText}>
                  Save this version
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

ReferenceDocumentsPanel.propTypes = {
  simulationId: PropTypes.string.isRequired,
  onChanged: PropTypes.func,
};

export default ReferenceDocumentsPanel;
